import type { Row } from '@tanstack/react-table';

import { useTranslation } from 'react-i18next';

import type { ContactLanguage } from '~/api/clients/types';
import type { Product } from '~/api/products/types';
import type { AppTableFeatures } from '~/components/Table/Table.hooks';

import { useUser } from '~/api/user';
import { Separator, Table } from '~/components';
import { ProductStatusTag } from '~/features/products';
import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import { getProductName } from '../../FormLines.utils';
import {
  mobileActionsStyle,
  mobileCellStyle,
  mobileFooterStyle,
  mobileHeaderStyle,
  mobileNameStyle,
  mobileSeparatorStyle,
  mutedCellStyle,
  priceCellStyle,
  truncatedCellStyle,
} from './ProductPickerDialog.css';
import { ProductPickerOptionsCell } from './ProductPickerOptionsCell';

type Props = {
  language: ContactLanguage;
  row: Row<AppTableFeatures, Product>;
};

export const ProductPickerMobileCell = ({ language, row }: Props) => {
  const product = row.original;

  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['lines']);

  const { data: user } = useUser();

  return (
    <div className={mobileCellStyle}>
      <div className={mobileHeaderStyle}>
        <Table.SelectRow row={row} />

        <div className={clsx(mobileNameStyle, product.status === 'Rejected' && mutedCellStyle)}>
          {getProductName(product, language, user.lang)}
        </div>

        <div className={mobileActionsStyle}>
          <ProductStatusTag status={product.status} />

          <ProductPickerOptionsCell product={product} />
        </div>
      </div>

      <Separator className={mobileSeparatorStyle} />

      <div className={mobileFooterStyle}>
        <div className={clsx(truncatedCellStyle, mutedCellStyle)}>{t(`lines:creativeWork.options.${product.activityType}.label`)}</div>

        <div className={priceCellStyle}>{product.price ? formatCurrency(product.price) : '-'}</div>
      </div>
    </div>
  );
};
